import React from "react";
import { Gift } from "lucide-react";
import TipDialog from "./TipDialog";

// EndOfVideoTipPrompt — Iter-58.9 (2026-06-02).
// Renders a "🎁 Tip @username" CTA overlaid on the player during the
// last ~3 seconds of every video. Auto-dismisses when the video ends
// (handleVideoEnded swaps to the next clip and unmounts us with it).
//
// Why "last 3 seconds" instead of "after end":
//   • Showing AFTER the video ends competes with the auto-skip-next.
//     The new clip is already mounting; a CTA pinned to the OLD clip
//     would either flash + vanish or fight z-index with the new frame.
//   • Catching the viewer DURING the last beat of content (when they
//     are still engaged with the creator on-screen) converts better
//     and feels like a natural beat — same pattern YouTube uses for
//     end-screens and TikTok uses for follow-prompt overlays.
//
// Visibility rules:
//   • Skipped when the player is empty or playing your OWN video.
//   • Skipped when the video is shorter than 5s (no room for a 3s
//     overlay without obscuring most of the clip).
//   • Skipped if a logged-out user is watching — the click goes to
//     `/pricing` which triggers auth anyway, but on mobile the tap
//     hijack is worse UX than just showing it after sign-in.
//
// Click path:
//   • Open the in-app TipDialog modal (no nav, no /pricing redirect).
//     The dialog lets the user pick 1/5/10/25 cr and posts to /api/tips
//     directly. If the user can't afford the selected tier, the
//     dialog flips its primary CTA to "Top up X cr →" with a clear
//     shortfall amount — guidance is always explicit, never silent.
const TIP_WINDOW_SECONDS = 3.0;
const MIN_VIDEO_DURATION_FOR_PROMPT = 5.0;

const EndOfVideoTipPrompt = ({ videoRef, video, user, onDismiss }) => {
  const [visible, setVisible] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const producer = video?.username;
  const isOwnVideo = user && producer && user.username === producer;
  // Logged-out viewers: showing this would route them to /pricing
  // anyway, but the auth gate is jarring mid-video. Hold the prompt
  // until they're signed in.
  const eligible = !!producer && !isOwnVideo && !!user;

  React.useEffect(() => {
    if (!eligible) {
      setVisible(false);
      return undefined;
    }
    const node = videoRef.current;
    if (!node) return undefined;
    setVisible(false);

    const onTime = () => {
      const dur = node.duration;
      if (!Number.isFinite(dur) || dur < MIN_VIDEO_DURATION_FOR_PROMPT) return;
      const remaining = dur - node.currentTime;
      // Use the functional updater to avoid a stale-closure flip-flop
      // when timeupdate fires faster than React can re-render.
      const shouldShow = remaining > 0 && remaining <= TIP_WINDOW_SECONDS;
      setVisible((cur) => (cur === shouldShow ? cur : shouldShow));
    };
    const onEnded = () => setVisible(false);
    // Reset visibility when src changes (new video swapped in).
    const onLoaded = () => setVisible(false);

    node.addEventListener("timeupdate", onTime);
    node.addEventListener("ended", onEnded);
    node.addEventListener("loadedmetadata", onLoaded);
    return () => {
      node.removeEventListener("timeupdate", onTime);
      node.removeEventListener("ended", onEnded);
      node.removeEventListener("loadedmetadata", onLoaded);
    };
  }, [eligible, videoRef, video?.id]);

  if (!eligible) return null;

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Open the tip dialog directly. We deliberately do NOT pause the
    // video here — the modal overlays and pauses focus on its own.
    setDialogOpen(true);
  };

  return (
    <>
      {visible && (
        <div
          data-testid="end-of-video-tip-prompt"
          // Pinned to the bottom of the player wrap. `pointer-events-auto`
          // so the button is clickable; the wrapper has `pointer-events-
          // none` everywhere else so the play/pause tap-overlay still
          // works during this 3-second window.
          className="absolute inset-x-0 bottom-0 z-30 flex justify-center pointer-events-none animate-in fade-in slide-in-from-bottom-2 duration-300"
        >
          <button
            type="button"
            onClick={handleClick}
            data-testid="end-of-video-tip-cta"
            className="pointer-events-auto mb-3 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-400 hover:to-rose-400 text-gray-900 font-bold text-sm shadow-2xl shadow-amber-500/40 backdrop-blur-sm transition-all hover:scale-105 active:scale-95"
            style={{
              // Apple HIG-compliant tap target.
              minHeight: "44px",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            <Gift className="h-4 w-4" />
            <span>
              Tip <span className="text-white drop-shadow">@{producer}</span>
            </span>
          </button>
        </div>
      )}
      <TipDialog
        open={dialogOpen}
        onOpenChange={(o) => {
          setDialogOpen(o);
          if (!o) onDismiss?.();
        }}
        recipientUsername={producer}
      />
    </>
  );
};

export default EndOfVideoTipPrompt;
